import React from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Link } from '@tanstack/react-router'
import { CreditCard, Key, ArrowRight, CheckCircle } from 'lucide-react'
import { useSubscription } from '@/hooks/use-subscription'

interface ApiKeyGuideProps {
  variant?: 'card' | 'inline' | 'compact'
  title?: string
  description?: string
  showSteps?: boolean
  className?: string
}

export function ApiKeyGuide({ 
  variant = 'inline', 
  title = "Get Your API Key",
  description = "API keys are automatically generated when you purchase a subscription plan",
  showSteps = true,
  className = ""
}: ApiKeyGuideProps) {
  const { subscription, loading: subscriptionLoading } = useSubscription()

  // Check if user has an active subscription
  const hasActiveSubscription = subscription && 
    (subscription.status === 'active' || subscription.plan_type !== 'free')

  if (subscriptionLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  // If user has active subscription, show link to API keys page
  if (hasActiveSubscription) {
    if (variant === 'compact') {
      return (
        <Button asChild className={className}>
          <Link to="/dashboard/api-keys">
            <Key className="w-4 h-4 mr-2" />
            View API Keys
          </Link>
        </Button>
      )
    }

    return (
      <div className={`text-center py-6 ${className}`}>
        <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          You're all set!
        </h3>
        <p className="text-gray-600 mb-4">
          Your API keys are ready to use. Manage them in your dashboard.
        </p>
        <Button asChild>
          <Link to="/dashboard/api-keys">
            <Key className="w-4 h-4 mr-2" />
            View API Keys
          </Link>
        </Button>
      </div>
    )
  }

  // User doesn't have active subscription - show guide
  const steps = [
    {
      number: 1,
      title: "Choose a Plan",
      description: "Select a subscription plan that fits your needs"
    },
    {
      number: 2,
      title: "Complete Purchase",
      description: "Securely complete your subscription purchase"
    },
    {
      number: 3,
      title: "Get Your API Key",
      description: "Your API key will be automatically generated and ready to use"
    },
    {
      number: 4,
      title: "Start Using MCP",
      description: "Access our MCP server at https://mcp.plusefin.com/mcp?apikey=your_api_key to begin using AI-powered financial tools"
    }
  ]

  if (variant === 'compact') {
    return (
      <Button asChild className={className}>
        <Link to="/dashboard/billing">
          <CreditCard className="w-4 h-4 mr-2" />
          Get API Key
        </Link>
      </Button>
    )
  }

  if (variant === 'card') {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="w-5 h-5" />
            {title}
          </CardTitle>
          <CardDescription>
            {description}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {showSteps && (
            <div className="space-y-4 mb-6">
              {steps.map((step, index) => (
                <div key={step.number} className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-medium">
                    {step.number}
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-gray-900">{step.title}</h4>
                    <p className="text-sm text-gray-600">{step.description}</p>
                  </div>
                  {index < steps.length - 1 && (
                    <ArrowRight className="w-4 h-4 text-gray-400 mt-1" />
                  )}
                </div>
              ))}
            </div>
          )}
          <Button asChild className="w-full">
            <Link to="/dashboard/billing">
              <CreditCard className="w-4 h-4 mr-2" />
              View Plans & Get Started
            </Link>
          </Button>
        </CardContent>
      </Card>
    )
  }

  // Default inline variant
  return (
    <div className={`text-center py-8 ${className}`}>
      <Key className="w-12 h-12 text-gray-400 mx-auto mb-4" />
      <h3 className="text-lg font-medium text-gray-900 mb-2">
        {title}
      </h3>
      <p className="text-gray-600 mb-6">
        {description}
      </p>
      
      {showSteps && (
        <div className="max-w-md mx-auto mb-6">
          <div className="grid grid-cols-4 gap-4 text-sm">
            {steps.map((step, index) => (
              <div key={step.number} className="text-center">
                <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-medium mx-auto mb-2">
                  {step.number}
                </div>
                <h4 className="font-medium text-gray-900 mb-1">{step.title}</h4>
                <p className="text-gray-600 text-xs">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <Button asChild>
        <Link to="/dashboard/billing">
          <CreditCard className="w-4 h-4 mr-2" />
          View Plans & Get Started
        </Link>
      </Button>
    </div>
  )
}

export default ApiKeyGuide